// =============================================
// COMPONENTE: MODAL PORTAL
// =============================================
// Arquivo: ModalPortal.tsx
// Descrição: Portal para renderizar modais no body, garantindo z-index correto
// =============================================

import { useEffect, useRef, ReactNode } from 'react';
import { createPortal } from 'react-dom';

// =============================================
// INTERFACES
// =============================================

interface ModalPortalProps {
  children: ReactNode;
  isOpen: boolean;
}

// =============================================
// COMPONENTE PRINCIPAL
// =============================================

/**
 * ModalPortal - Renderiza modais diretamente no body usando React Portal
 *
 * Isso garante que os modais fiquem sempre por cima de todos os outros elementos,
 * independente do z-index de headers, sidebars, etc.
 *
 * @param children - Conteúdo do modal
 * @param isOpen - Se o modal está aberto
 * @returns Portal renderizado no body
 */
const ModalPortal: React.FC<ModalPortalProps> = ({ children, isOpen }) => {
  const modalRoot = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Criar ou obter o container de modais no body
    let container = document.getElementById('modal-root') as HTMLDivElement;

    if (!container) {
      container = document.createElement('div');
      container.id = 'modal-root';
      container.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 10000;
      `;
      document.body.appendChild(container);
    }

    modalRoot.current = container;

    // Prevenir scroll do body quando modal está aberto
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      if (isOpen) {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen]);

  // Só renderiza se estiver aberto
  if (!isOpen || !modalRoot.current) {
    return null;
  }

  // Cria um wrapper para permitir eventos de pointer
  const wrapper = (
    <div style={{ pointerEvents: 'auto', width: '100%', height: '100%' }}>
      {children}
    </div>
  );

  return createPortal(wrapper, modalRoot.current);
};

export default ModalPortal;
